import * as _ from "lodash";
import moment from "moment-timezone";

import { hourMinuteTimeSplit } from "src/library/utils/time-helpers/time-helpers";
import {
  IDeliveryCutoffSetting,
  IDeliveryDays,
  IDeliveryWeekLeadTimesSetting,
  ILeadTimeDeliveryWeek,
  WeekDay,
  IDeliveryRoute,
  WeekDayName
} from "src/core/drops/api/drops.api";
import { IProductLeadTimeGroup } from "src/core/products/api/products.api";
import {
  IListLine,
  IList
} from "src/core/lists/api/lists.api";
import { IOrder } from "src/core/orders/api/orders.api";

import { IOrderLine } from "../api/orders.api";

/**
 * Generate the default cutoff configuration to deliver a product with lead time productCutOff.
 *
 * The default setting is calculated assuming:
 *  - A product with cutoffDays X can be ordered X days before the
 *    order delivery date.
 *  - Saturdays and Sundays are not counted in.
 *  - The default cut off time is 14:00.
 *
 * @param productCutOff - product lead time to generate default settings.
 */
export const getLeadTimeDefaultSetting = (productCutOff: number, defaultCutOff?: IDeliveryCutoffSetting): ILeadTimeDeliveryWeek => {
  const deliveryWeekLeadTimes: ILeadTimeDeliveryWeek = {} as ILeadTimeDeliveryWeek;

  const weekDays: WeekDay[] = [
    1,
    2,
    3,
    4,
    5,
    6,
    7
  ];

  weekDays.map((weekDay: WeekDay) => {
    deliveryWeekLeadTimes[ weekDay ] = {
      // We assume Friday in the maximun cut-off for an order.
      day: getDefaultLeadTimeCutOff(productCutOff, weekDay),
      cutoffTime: getDefaultCutOff(defaultCutOff)
    };
  });

  return deliveryWeekLeadTimes;
};

/**
 * Get the default cutoff time.
 *
 * @param defaultCutOff - The default cut-off (inherited) setting.
 */
export const getDefaultCutOff = (defaultCutOff?: IDeliveryCutoffSetting) => {
  return defaultCutOff && defaultCutOff.time ? defaultCutOff.time : undefined;
};

/**
 * Get the default week day cutoff for a product with lead time productCutOff.
 *
 * @param productCutOff - the product cut off.
 * @param weekDay - the week day the product will be delivered.
 *
 * @returns - Cutoff day (week day).
 */
export const getDefaultLeadTimeCutOff = (productCutOff: number, weekDay: WeekDay): WeekDay => {
  let deliveryDay = weekDay - productCutOff;

  deliveryDay = deliveryDay <= 0 ? deliveryDay + 7 : deliveryDay;

  return (deliveryDay <= 5 ? deliveryDay : 5) as WeekDay;
};

/**
 *
 * Get number of days left to order, taking into account weekday leadtimes
 *
 * @param {String} cutoff - The customer cutoff time - e.g. 14.00h.
 * @param {Number} deliveryDate - the order delivery date.
 * @param {Object} weekLeadTimes - ordering lead time for each day of the week (it
 *  reflects the cutoff week day of an order with a product with leadTime = 1 need).
 * { 1: 6, 2: 1, 3: 2, 4: 3, 5: 4... } means:
 *   - Cutoff for delivery on Monday is Friday.
 *   - Cutoff for delivery on Tuesday is Monday.
 *   - Cutoff for delivery on Wednesday is Tuesday.
 *   - and so on.
 *
 */
export const getAllowedDays = (cutoff: IDeliveryCutoffSetting, deliveryDate: Date, weekLeadTimes: IDeliveryWeekLeadTimesSetting, timezone: string): number => {
  const deliverydateMoment = moment.utc(deliveryDate);
  // @ts-ignore
  const deliveryDOW: WeekDay = deliverydateMoment.isoWeekday();
  const orderByDay = weekLeadTimes.deliveryWeekLeadTimes[ deliveryDOW ];
  // TODO:
  // @ts-ignore:
  const leadTime = (deliveryDOW - orderByDay + 7) % 7;
  const deliveryCutOff = deliverydateMoment.subtract(leadTime, "days");

  // Days until order cut-off.
  return daysToDeliveryDateCutOff(deliveryCutOff.toDate(), cutoff.time, timezone);
};

export const unavailableDeliveryDays = (deliveryRoute: IDeliveryRoute): string[] => {
  const daysOfWeek: WeekDayName[] = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday"
  ];

  return _.difference(daysOfWeek, _.keys(deliveryRoute.deliveryDays));
};

/**
 * Complete the lead time configuration with default values for
 * unknow product lead time settings.
 *
 * It will ensure the screen settings are complete for every product lead time.
 *
 * @param leadTimes - existing lead times.
 * @param leadTimeConfiguration - existing lead time configuration.
 * @param defaultCutOff - used to set the default cutoff time for each day
 */
export const completeLeadTimeConfiguration = (
  leadTimes: IProductLeadTimeGroup[],
  leadTimeConfiguration: null | IDeliveryWeekLeadTimesSetting,
  defaultCutOff?: IDeliveryCutoffSetting
): IDeliveryWeekLeadTimesSetting => {
  let completedLeadTimeConfiguration: IDeliveryWeekLeadTimesSetting | null = leadTimeConfiguration;

  if (!completedLeadTimeConfiguration || !completedLeadTimeConfiguration.deliveryWeekLeadTimes) {
    completedLeadTimeConfiguration = { deliveryWeekLeadTimes: {} };
  }

  leadTimes.map(leadTime => {
    const leadTimeString = leadTime.name.toString();
    const leadTimeDetails = getLeadTimeDefaultSetting(leadTime.defaultLeadTimeDays, defaultCutOff);

    if (completedLeadTimeConfiguration) {
      if (!completedLeadTimeConfiguration.deliveryWeekLeadTimes[ leadTimeString ]) {
        completedLeadTimeConfiguration.deliveryWeekLeadTimes[ leadTimeString ] = leadTimeDetails;
      } else {
        for (const day in completedLeadTimeConfiguration.deliveryWeekLeadTimes[ leadTimeString ]) {
          if (!completedLeadTimeConfiguration.deliveryWeekLeadTimes[ leadTimeString ][ day ].cutoffTime) {
            completedLeadTimeConfiguration.deliveryWeekLeadTimes[ leadTimeString ][ day ].cutoffTime = defaultCutOff?.time;
          }
        }
      }
    }
  });

  return completedLeadTimeConfiguration as IDeliveryWeekLeadTimesSetting;
};

/**
 * Get the time to a delivery cut off in DAYS.
 *
 * 1 days means, there is still time. Even if there are only a few hours left.
 *
 * Examples:
 *  - 30 seconds to cut-off => 0 day
 *  - 1 hour to cut-off => 0 day
 *  - 25 hours to cut-off => 1 days
 *
 * @param deliveryCutOff - The delivery day date.
 * @param cutOffTime - The cutoff time (format HH:mm).
 *
 * @return - The number of days left to cut-off.
 */
export const daysToDeliveryDateCutOff = (deliveryCutOff: Date, cutOffTime: string, cutoffTimezone: string) => {
  const timeToCutOffSeconds = secondsToDeliveryDateCutOff(deliveryCutOff, cutOffTime, cutoffTimezone);
  const cutOffTimeHour = parseInt(cutOffTime.split(":")[ 0 ], 10);
  const cutOffTimeMinute = parseInt(cutOffTime.split(":")[ 1 ], 10);

  // if under 1 day return 0
  if (timeToCutOffSeconds < 86400) {
    return 0;
  }

  const secondsToDay = 60 * 60 * 24;

  // round dependant if we are before or after cutoff for the current day
  if (
    moment.utc().isBefore(
      moment
        .tz(cutoffTimezone)
        .hours(cutOffTimeHour)
        .minutes(cutOffTimeMinute)
    )
  ) {
    return Math.floor(timeToCutOffSeconds / secondsToDay);
  }

  return Math.ceil(timeToCutOffSeconds / secondsToDay);
};

/**
 * Get the time to a delivery cut off in SECONDS.
 *
 * @param deliveryCutOff - The delivery day date.
 * @param cutOffTime - The cutoff time (format HH:mm).
 *
 * @return - The number of seconds left to cut-off.
 */
export const secondsToDeliveryDateCutOff = (deliveryCutOff: Date, cutOffTime: string, cutoffTimezone: string): number => {
  const cutOffTimeHour = parseInt(cutOffTime.split(":")[ 0 ], 10);
  const cutOffTimeMinute = parseInt(cutOffTime.split(":")[ 1 ], 10);

  const deliveryTime = moment.tz(deliveryCutOff, cutoffTimezone)
    .hour(cutOffTimeHour)
    .minute(cutOffTimeMinute);

  const now = moment.tz(cutoffTimezone);

  return deliveryTime.diff(now, "seconds");
};

/**
 * Get number of days left for an orderlines cutoff.
 *
 * @param {IOrderLine} orderLine - orderline object.
 * @param {IDeliveryCutoffSetting} partOrder
 *
 * @return - Whether the date has passed or not.
 */
export const productCutOffTime = (productLeadTimeGroup: IProductLeadTimeGroup, cutoff: string, deliveryDate: Date, timezone: string): Date => {
  const { hours, minutes } = hourMinuteTimeSplit(cutoff);

  return moment
    .tz(deliveryDate, timezone)
    .set({
      hours,
      minutes
    })
    .subtract(productLeadTimeGroup.defaultLeadTimeDays, "days")
    .toDate();
};

/**
 * For a given order, return the maximum lead time in days.
 *
 * @param order
 *
 * @returns {number} - Max lead time.
 */
export const getMaxLeadTime = (orderOrList: IOrder | IList, leadTimeGroups: IProductLeadTimeGroup[]): IProductLeadTimeGroup | null => {
  let max: IProductLeadTimeGroup | null = null;
  // @ts-ignore
  const lines = orderOrList.orderLines ? orderOrList.orderLines : orderOrList.listLines ? orderOrList.listLines : [];

  if (lines && lines.length > 0) {
    lines.map((orderLine: IOrderLine | IListLine) => {
      const leadTimeGroup = _.find(leadTimeGroups, ltg => ltg.id === orderLine.product.leadTimeGroupId);

      if ((max === null || leadTimeGroup?.defaultLeadTimeDays && leadTimeGroup?.defaultLeadTimeDays > max.defaultLeadTimeDays) && leadTimeGroup) {
        max = leadTimeGroup;
      }
    });
  }

  return max;
};

/**
 * Get the name of a week a day.
 *
 * @param day - Number of the week (1-7).
 *
 * @return - the name of the day of the week.
 */
export const getDay = (day: number): keyof IDeliveryDays | null => {
  switch (day) {
    case 1:
      return "Monday";

    case 2:
      return "Tuesday";
    case 3:
      return "Wednesday";

    case 4:
      return "Thursday";

    case 5:
      return "Friday";

    case 6:
      return "Saturday";

    case 7:
      return "Sunday";

    default:
      return null;
  }
};

/**
 *
 * @param date
 */
export const getWeekDayFromDate = (date: Date | moment.Moment): WeekDay => {
  return date instanceof moment ? ((date as moment.Moment).isoWeekday() as WeekDay) : (moment.utc(date).isoWeekday() as WeekDay);
};
